import React from 'react';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import { DocumentType } from 'src/utils/enum';
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import { dt } from 'src/utils/documentsI18n';
import 'src/components/documentostributarios/ModalDeleteObligations.scss';

/**
 * Cuerpo del modal de confirmación al deshacer un match (mismo patrón que ModalDeleteObligations).
 */
export default function DeleteReconciliationModalContent({
  documentLabel,
  counterpartName,
  amount,
  currency = 'CLP',
  matchDate,
  willRemoveObligation = false,
  bankTransactions = [],
}) {
  const formattedDate =
    matchDate && moment(matchDate).isValid()
      ? moment(matchDate).format('DD/MM/YYYY')
      : null;
  const banks = Array.isArray(bankTransactions) ? bankTransactions.filter((tx) => tx?.id) : [];

  return (
    <div className="delete-obligation-modal-body">
      <div className="delete-obligation-modal-icon" aria-hidden>
        <FeatherIcon icon="trash-2" size={22} />
      </div>

      <p className="delete-obligation-modal-lead">
        ¿Deseas eliminar <strong>este match</strong>? La acción no se puede deshacer.
      </p>

      <div className="delete-obligation-modal-summary">
        {documentLabel && (
          <div className="delete-obligation-modal-summary-type">{documentLabel}</div>
        )}
        {counterpartName && (
          <div className="delete-obligation-modal-summary-row">
            <span className="delete-obligation-modal-summary-label">Contraparte</span>
            <span className="delete-obligation-modal-summary-value">{counterpartName}</span>
          </div>
        )}
        {amount != null && (
          <div className="delete-obligation-modal-summary-row">
            <span className="delete-obligation-modal-summary-label">Monto conciliado</span>
            <span className="delete-obligation-modal-summary-value">
              {formatMoneyWithCurrency(amount, currency)}
            </span>
          </div>
        )}
        {formattedDate && (
          <div className="delete-obligation-modal-summary-row">
            <span className="delete-obligation-modal-summary-label">Fecha</span>
            <span className="delete-obligation-modal-summary-value">{formattedDate}</span>
          </div>
        )}
      </div>

      {banks.length > 0 ? (
        <div className="delete-obligation-modal-warning">
          <p>{dt('reconcile.statementLinkedUnmatchBankWarning')}</p>
          <ul>
            {banks.map((tx) => {
              const date =
                tx.date && moment(tx.date).isValid() ? moment(tx.date).format('DD/MM/YYYY') : '';
              const description = String(tx.description || '').trim();
              return (
                <li key={tx.id}>
                  {[date, description].filter(Boolean).join(' — ') || tx.id}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <p className="delete-obligation-modal-warning">
        {willRemoveObligation
          ? 'La obligación vinculada a esta conciliación también se eliminará del listado. '
          : ''}
        Se revertirá el saldo remanente del movimiento y los matches asociados.
      </p>
    </div>
  );
}

const BH_RETENTION_META_TYPES = new Set([
  'retencionboletashonorarios',
  'retencion_boletas_honorarios',
]);

const DONT_HIDE_ON_UNMATCH_META_TYPES = new Set([
  ...BH_RETENTION_META_TYPES,
  'tarjetadecreditonacional',
  'tarjetadecreditointernacional',
]);

/** Obligación creada por una regla (`creationFor: rule` o label `REGLA:`). */
export function isRuleCreatedObligation(match) {
  if (String(match?.meta?.creationFor ?? '').toLowerCase() === 'rule') return true;

  const labels = Array.isArray(match?.labels) ? match.labels : [];
  return labels.some((label) => String(label ?? '').toUpperCase().startsWith('REGLA:'));
}

/** Alineado con deleteObligationMatch: se oculta la creada para el match y la creada por regla. */
export function shouldDeleteObligationOnUnmatch(match) {
  if (match?.documentType !== DocumentType.OBLIGATION) return false;
  if (DONT_HIDE_ON_UNMATCH_META_TYPES.has(String(match?.meta?.type ?? '').toLowerCase())) return false;

  const creationFor = String(match?.meta?.creationFor ?? '').toLowerCase();
  return creationFor === 'match' || isRuleCreatedObligation(match);
}

export function shouldWarnObligationRemovalOnUnmatch(match) {
  return shouldDeleteObligationOnUnmatch(match);
}
